import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import { ArrowRight, Quote, Loader2 } from 'lucide-react';
import { AnimatedSection, WhatsAppButton, ScrollRevealBeforeAfter } from '@/components/common';
import { cn } from '@/lib/utils';
import { heroTextReveal, staggerContainer, staggerItem } from '@/lib/animations';
import { useRef, useState, useEffect } from 'react';
import { featuredCategoriesApi, featuredProductsApi, FeaturedProductItem } from '@/services/api';
import type { FeaturedCategoryItem } from '@/types';

// Profesyonel müşteri yorumları
const TESTIMONIALS = [
  {
    name: 'Ayşe Yılmaz',
    title: 'Kurucu Mimar',
    company: 'AY Mimarlık',
    text: 'Turco ile 5 yılı aşkın süredir çalışıyoruz. Kalite standardı, teslimat güvenilirliği ve profesyonel iletişim konusunda sektörün en iyisi.',
    image: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?q=80&w=200&auto=format&fit=crop',
  },
  {
    name: 'Mehmet Kaya',
    title: 'Genel Müdür',
    company: 'Kaya Hotels Group',
    text: '500 odanın mobilyasını Turco ile tamamladık. 3 yıl sonra hala ilk günkü kalitede. Profesyonel B2B süreç yönetimi için referans firma.',
    image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?q=80&w=200&auto=format&fit=crop',
  },
  {
    name: 'Zeynep Demir',
    title: 'İç Mimar',
    company: 'ZD Design Studio',
    text: 'Özel üretim projelerinde Turco\'nun esnekliği ve teknik kapasitesi rakipsiz. Her projede beklentilerin ötesinde sonuçlar alıyoruz.',
    image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?q=80&w=200&auto=format&fit=crop',
  },
];

// Fallback placeholder image
const PLACEHOLDER_IMAGE = 'https://images.unsplash.com/photo-1555041469-a586c61ea9bc?w=800&q=80';

// Hero Videos Configuration
const HERO_VIDEOS = [
  {
    id: 1,
    src: "/videos/luxury-brand-film.mp4",
    poster: "https://images.unsplash.com/photo-1618221195710-dd6b41faaea6?q=80&w=2000&auto=format&fit=crop"
  },
  {
    id: 2,
    src: "/videos/craftsman-hands.mp4#t=3", // Start after 3 seconds to show Turco Chair part
    poster: "https://images.unsplash.com/photo-1616486338812-3dadae4b4ace?q=80&w=1200&auto=format&fit=crop"
  }
];

export function HomePage() {
  const heroRef = useRef<HTMLElement>(null);
  const carouselRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  const [featuredCategories, setFeaturedCategories] = useState<FeaturedCategoryItem[]>([]);
  const [featuredProducts, setFeaturedProducts] = useState<FeaturedProductItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [productsLoading, setProductsLoading] = useState(true);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);

  // Video Carousel Timer
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentVideoIndex((prev) => (prev + 1) % HERO_VIDEOS.length);
    }, 8000); // Switch every 8 seconds

    return () => clearInterval(timer);
  }, []);

  // Load featured categories from API
  useEffect(() => {
    const loadFeaturedCategories = async () => {
      try {
        const data = await featuredCategoriesApi.getAll();
        setFeaturedCategories(data);
      } catch (error) {
        console.warn('Featured categories could not be loaded:', error);
      } finally {
        setLoading(false);
      }
    };

    loadFeaturedCategories();
  }, []);

  // Load featured products from API
  useEffect(() => {
    const loadFeaturedProducts = async () => {
      try {
        const data = await featuredProductsApi.getAll();
        setFeaturedProducts(data);
      } catch (error) {
        console.warn('Featured products could not be loaded:', error);
      } finally {
        setProductsLoading(false);
      }
    };

    loadFeaturedProducts();
  }, []);

  // Update carousel width when featured categories change
  useEffect(() => {
    if (carouselRef.current && featuredCategories.length > 0) {
      // Small delay to ensure images are rendered
      const timer = setTimeout(() => {
        if (carouselRef.current) {
          setWidth(carouselRef.current.scrollWidth - carouselRef.current.offsetWidth);
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [featuredCategories]);

  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ['start start', 'end start'],
  });

  const heroOpacity = useTransform(scrollYProgress, [0, 0.5], [1, 0]);
  const heroScale = useTransform(scrollYProgress, [0, 0.5], [1, 1.1]);
  const heroY = useTransform(scrollYProgress, [0, 1], [0, 150]);

  return (
    <>
      {/* Document Metadata - React 19 native */}
      <title>Turco Sandalye | Premium Mobilya Üreticisi</title>
      <meta
        name="description"
        content="1985'ten bu yana seçkin mekanlar için premium mobilya üretimi. Türk ustalığı ve modern tasarımın birleşimi."
      />

      {/* WhatsApp Floating Button */}
      <WhatsAppButton message="Merhaba, Turco Sandalye ürünleri hakkında bilgi almak istiyorum." />

      {/* Hero Section - Clean & Minimal */}
      <section
        ref={heroRef}
        className="relative h-screen min-h-[700px] flex items-end overflow-hidden"
      >
        {/* Background Video Slider with Crossfade */}
        <div className="absolute inset-0 z-0 bg-black">
          <AnimatePresence mode="popLayout">
            <motion.div
              key={currentVideoIndex}
              style={{ scale: heroScale, y: heroY }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 1.5, ease: "easeInOut" }}
              className="absolute inset-0"
            >
              <video
                key={`video-${currentVideoIndex}`} // Force remount on change
                autoPlay
                muted
                loop
                playsInline
                poster={HERO_VIDEOS[currentVideoIndex].poster}
                className="w-full h-full object-cover"
              >
                <source src={HERO_VIDEOS[currentVideoIndex].src} type="video/mp4" />
              </video>
              {/* Subtle overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent" />
            </motion.div>
          </AnimatePresence>
        </div>

        {/* Hero Content - Bottom aligned like Vitra */}
        <motion.div
          style={{ opacity: heroOpacity }}
          className="relative z-10 w-full pb-16 lg:pb-24"
        >
          <div className="container mx-auto px-6 lg:px-8">
            <motion.div
              initial="initial"
              animate="animate"
              variants={staggerContainer}
              className="max-w-4xl"
            >
              {/* Headline - Large & Clean */}
              <motion.h1
                variants={heroTextReveal}
                className="font-display text-3xl sm:text-5xl lg:text-6xl xl:text-7xl font-medium
                           leading-[1.1] text-white mb-6"
              >
                Premium Mobilya Üretimi
              </motion.h1>

              {/* Subheadline */}
              <motion.p
                variants={staggerItem}
                className="text-lg sm:text-xl text-white/80 leading-relaxed max-w-2xl mb-8"
              >
                1985'ten bu yana seçkin mekanlar için özel üretim.
                Türk ustalığı ve modern tasarımın birleşimi.
              </motion.p>

              {/* Minimal CTA */}
              <motion.div variants={staggerItem}>
                <Link
                  to="/products"
                  className={cn(
                    'inline-flex items-center gap-2 text-white font-medium',
                    'border-b border-white/50 pb-1',
                    'transition-all duration-300',
                    'hover:border-gold hover:text-gold hover:gap-3',
                    'group'
                  )}
                >
                  Koleksiyonu İncele
                  <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                </Link>
              </motion.div>
            </motion.div>
          </div>
        </motion.div>

        {/* Scroll hint - minimal */}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20">
          <motion.div
            animate={{ y: [0, 8, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
            className="w-6 h-10 border border-white/30 flex items-start justify-center pt-2"
          >
            <motion.div
              animate={{ y: [0, 12, 0], opacity: [1, 0.3, 1] }}
              transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
              className="w-0.5 h-3 bg-white/60"
            />
          </motion.div>
        </div>
      </section>

      {/* Koleksiyon Section - Dynamic Featured Categories */}
      <section id="products" className="py-16 md:py-24 bg-white border-b border-gray-100 overflow-hidden">
        <div className="container mx-auto px-4 lg:px-12">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-8 md:mb-12 gap-4">
            <div>
              <h2 className="font-display text-2xl md:text-3xl lg:text-4xl text-charcoal mb-2 md:mb-3">
                Koleksiyon
              </h2>
              <p className="text-gray-500 text-sm md:text-base max-w-md">Estetik ve konforun buluştuğu özel tasarımlarımızı keşfedin.</p>
            </div>
            <Link
              to="/products"
              className="inline-flex items-center gap-2 text-sm font-medium text-charcoal hover:text-gray-600 transition-colors group self-start sm:self-auto"
            >
              Tümünü Gör
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>

          {/* Loading State */}
          {loading ? (
            <div className="flex items-center justify-center h-[300px] md:h-[400px]">
              <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
            </div>
          ) : featuredCategories.length === 0 ? (
            // Fallback if no featured categories
            <div className="text-center py-16 text-gray-500">
              Henüz öne çıkan kategori eklenmemiş.
            </div>
          ) : (
            /* Framer Motion Drag Carousel - 10 Categories Grid */
            <motion.div ref={carouselRef} className="cursor-grab active:cursor-grabbing overflow-hidden -mx-4 px-4 md:-mx-0 md:px-0" whileTap={{ cursor: "grabbing" }}>
              <motion.div
                drag="x"
                dragConstraints={{ right: 0, left: -width }}
                dragElastic={0.08}
                dragTransition={{ bounceStiffness: 400, bounceDamping: 30 }}
                className="flex gap-3 md:gap-4 lg:gap-5 pb-4"
              >
                {featuredCategories.map((item, index) => (
                  <motion.div
                    key={item.id}
                    className="flex-shrink-0 w-[140px] sm:w-[160px] md:w-[180px] lg:w-[200px] xl:w-[220px] relative group"
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                  >
                    <Link
                      to={`/products?categorySlug=${item.category.slug}`}
                      className="block relative w-full aspect-[3/4] overflow-hidden rounded-lg bg-gray-100"
                      draggable="false"
                    >
                      <img
                        src={item.category.image?.url || PLACEHOLDER_IMAGE}
                        alt={item.category.name}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105 pointer-events-none select-none"
                        loading={index < 5 ? "eager" : "lazy"}
                      />
                      {/* Gradient Overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-80 group-hover:opacity-90 transition-opacity" />

                      {/* Content */}
                      <div className="absolute bottom-0 left-0 right-0 p-3 md:p-4">
                        <h3 className="font-display text-sm md:text-base lg:text-lg text-white line-clamp-2">{item.category.name}</h3>
                      </div>

                      {/* Hover Arrow - Desktop only */}
                      <div className="hidden md:flex absolute top-3 right-3 w-8 h-8 bg-white/90 rounded-full items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 transform translate-y-2 group-hover:translate-y-0">
                        <ArrowRight className="w-4 h-4 text-charcoal" />
                      </div>
                    </Link>
                  </motion.div>
                ))}
              </motion.div>
            </motion.div>
          )}

          {/* Mobile Scroll Hint */}
          <div className="flex md:hidden items-center justify-center mt-4 gap-2 text-gray-400 text-xs">
            <span>Kaydırarak keşfedin</span>
            <ArrowRight className="w-3 h-3" />
          </div>
        </div>
      </section>

      {/* Before/After Comparison Section - Scroll Reveal */}
      <ScrollRevealBeforeAfter
        beforeImage="/images/before-frame.png"
        afterImage="/images/after-finished.png"
      />

      {/* Featured Products Section */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-4 lg:px-12">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between mb-10 md:mb-14 gap-4">
            <div>
              <span className="text-gray-500 text-xs font-semibold tracking-widest uppercase mb-3 block">Seçili Ürünler</span>
              <h2 className="font-display text-2xl md:text-3xl lg:text-4xl text-charcoal">
                Öne Çıkanlar
              </h2>
            </div>
            <Link
              to="/products?featured=true"
              className="inline-flex items-center gap-2 text-sm font-medium text-charcoal hover:text-gray-600 transition-colors group self-start sm:self-auto"
            >
              Tümünü Gör
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>

          {productsLoading ? (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 lg:gap-8">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="animate-pulse">
                  <div className="aspect-[3/4] bg-gray-200 mb-4" />
                  <div className="h-4 bg-gray-200 w-3/4 mb-2" />
                  <div className="h-3 bg-gray-200 w-1/2" />
                </div>
              ))}
            </div>
          ) : featuredProducts.length === 0 ? (
            <div className="text-center py-16 text-gray-500">
              Henüz öne çıkan ürün eklenmemiş.
            </div>
          ) : (
            <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 lg:gap-8">
              {featuredProducts.slice(0, 6).map((item, index) => (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                >
                  <Link
                    to={`/products/${item.product.slug}`}
                    className="group block"
                  >
                    <div className="relative aspect-[3/4] overflow-hidden bg-gray-100 mb-4">
                      <img
                        src={item.product.image?.url || PLACEHOLDER_IMAGE}
                        alt={item.product.title}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                        loading={index < 3 ? "eager" : "lazy"}
                      />
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors duration-300" />

                      {/* Quick View Button - Desktop */}
                      <div className="hidden md:flex absolute bottom-4 left-4 right-4 opacity-0 group-hover:opacity-100 transition-all duration-300 translate-y-2 group-hover:translate-y-0">
                        <span className="w-full py-3 bg-white text-charcoal text-sm font-medium text-center hover:bg-charcoal hover:text-white transition-colors">
                          İncele
                        </span>
                      </div>
                    </div>
                    <div>
                      <h3 className="font-display text-base md:text-lg text-charcoal group-hover:text-gray-600 transition-colors line-clamp-1">
                        {item.product.title}
                      </h3>
                      {item.product.categories?.[0] && (
                        <p className="text-gray-500 text-sm mt-1">
                          {item.product.categories[0].name}
                        </p>
                      )}
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Testimonials Section - Professional & Clean */}
      <AnimatedSection className="py-20 lg:py-28 bg-gray-50">
        <div className="container mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between mb-12">
            <h2 className="font-display text-2xl lg:text-3xl text-charcoal">
              Referanslarımız
            </h2>
            <Link
              to="/projects"
              className="hidden md:inline-flex items-center gap-2 text-sm text-charcoal hover:text-gold transition-colors group"
            >
              Projelerimizi Görüntüle
              <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>

          <div className="grid lg:grid-cols-3 gap-px bg-gray-200">
            {TESTIMONIALS.map((testimonial, index) => (
              <motion.div
                key={testimonial.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white p-8 lg:p-10"
              >
                {/* Quote icon */}
                <Quote className="w-8 h-8 text-gray-200 mb-6" />

                {/* Quote text */}
                <p className="text-gray-700 leading-relaxed mb-8 text-base">
                  {testimonial.text}
                </p>

                {/* Author - Professional layout */}
                <div className="flex items-center gap-4">
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-12 h-12 object-cover grayscale"
                  />
                  <div>
                    <p className="font-medium text-charcoal">{testimonial.name}</p>
                    <p className="text-gray-500 text-sm">{testimonial.title}, {testimonial.company}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </AnimatedSection>

      {/* CTA Section - Minimal & Corporate */}
      <section className="py-20 lg:py-28 bg-charcoal border-b border-white/10">
        <div className="container mx-auto px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="font-display text-3xl lg:text-4xl text-white mb-6">
              Projeniz İçin Teklif Alın
            </h2>
            <p className="text-white/70 mb-10 text-lg">
              Özel üretim ve toptan satış için bizimle iletişime geçin.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-8 py-4 bg-white text-charcoal font-medium hover:bg-gold transition-colors group"
              >
                İletişime Geçin
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </Link>
              <a
                href="tel:+905537942161"
                className="inline-flex items-center gap-2 text-white/80 hover:text-white transition-colors"
              >
                veya arayın: 0553 794 21 61
              </a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
